import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Breadcrumbs } from "components/common/Breadcrumbs";
import { Breadcrumb } from "components/common/Breadcrumbs/types";
import { FormDialog, selectOptionSchema } from "components/common/FormDialog";
import {
  FIELD_TYPES,
  FormDialogField,
  FormDialogProps,
  SelectOption
} from "components/common/FormDialog/types";
import { Head } from "components/common/Head";
import { KeyPairDialog } from "components/common/KeyPairDialog";
import { SectionToggleButton } from "components/common/SectionToggleButton";
import { Table } from "components/common/Table";
import {
  TABLE_SORTING_TYPES,
  TableColumn,
  TableRowActionsMenuItem
} from "components/common/Table/types";
import { useMount } from "hooks/useMount";
import { usePrevious } from "hooks/usePrevious";
import { useUnmount } from "hooks/useUnmount";
import { clustersActions } from "modules/clusters/slices";
import {
  areClusterTemplateSettingsLoadingSelector,
  clusterTemplatesSelector,
  clusterTemplateSettingsSelector,
  isClusterCreatingSelector,
  isClusterDeletingSelector,
  isClusterResizingSelector,
  tableClustersSelector
} from "modules/clusters/selectors";
import { ClusterSetting, TableCluster } from "modules/clusters/types";
import { enterprisesActions } from "modules/enterprises/slices";
import { organizationSelector } from "modules/enterprises/selectors";
import { instancesActions } from "modules/instances/slices";
import { selectOptionFlavorsSelector } from "modules/instances/selectors";
import { FLAVOR_TYPE } from "modules/instances/types";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { pollingActions } from "modules/polling/slices";
import { createPolling } from "modules/polling/utils";
import { projectsActions } from "modules/projects/slices";
import { projectSelector } from "modules/projects/selectors";
import { securityActions } from "modules/security/slices";
import {
  isKeyPairCreatingSelector,
  keyPairSelector,
  keyPairsSelector
} from "modules/security/selectors";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useParams } from "react-router-dom";
import { isString } from "typeGuards/isString";
import { downloadFile } from "utils/downloadFile";
import { getSelectOption } from "utils/getSelectOption";
import { validateName } from "utils/validateName";
import { NumberSchema, number, ref, string } from "yup";
import { API_PATH } from "../../axios";
import {
  DEFAULT_FLAVOR,
  ENTITY_NAME_LENGTH,
  ERROR_MESSAGES,
  MAX_NODE_COUNT,
  MIN_NODE_COUNT,
  REGEX,
  ROUTES,
  VOLUME_SIZE_STEP
} from "../../constants";
import { DIALOG_TYPES } from "./types";

const MIN_DOCKER_VOLUME_SIZE = 50;
const MIN_MASTER_COUNT = 1;
const MIN_AUTOSCALING_NODE_COUNT = 0;
const DEFAULT_CLUSTER_AUTO_SCALING = true;

const POLL_ID_PREFIX = "CLUSTERS";

const POLL_IDS = {
  clusters: "CLUSTERS",
  flavors: "FLAVORS",
  clusterTemplates: "CLUSTER_TEMPLATES",
  keyPairs: "KEY_PAIRS"
};

const tableColumns: TableColumn<TableCluster>[] = [
  { key: "name", label: "Name", width: "25%" },
  { key: "status", label: "Status", width: "20%" },
  { key: "coeVersion", label: "Kubernetes version", width: "10%" },
  {
    key: "masterCount",
    label: "Master count",
    sortingType: TABLE_SORTING_TYPES.NUMBER
  },
  {
    key: "nodeCount",
    label: "Node count",
    sortingType: TABLE_SORTING_TYPES.NUMBER
  },
  {
    key: "totalvCPUs",
    label: "Total vCPUs",
    sortingType: TABLE_SORTING_TYPES.NUMBER
  },
  {
    key: "totalMemory",
    label: "Total memory",
    sortingType: TABLE_SORTING_TYPES.NUMBER
  }
];

const title = "Clusters";

export const Clusters: FC = () => {
  const dispatch = useDispatch();
  const matchParams = useParams<{
    organizationId: string;
    projectId: string;
    regionId: string;
  }>();
  const organization = useSelector(organizationSelector);
  const project = useSelector(projectSelector);
  const flavors = useSelector(selectOptionFlavorsSelector);
  const default_flavor = flavors?.find((flavor) =>
    flavor.name.includes(DEFAULT_FLAVOR)
  );
  const keyPairs = useSelector(keyPairsSelector);
  const clusterTemplates = useSelector(clusterTemplatesSelector);
  const clusterTemplateSettings = useSelector(clusterTemplateSettingsSelector);
  const areClusterTemplateSettingsLoading = useSelector(
    areClusterTemplateSettingsLoadingSelector
  );
  const clusters = useSelector(tableClustersSelector);
  const [dialog, setDialog] = useState<{
    isOpened: boolean;
    type: DIALOG_TYPES;
  }>({ type: DIALOG_TYPES.CREATE, isOpened: false });
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedCreateClusterTemplateId, setSelectedCreateClusterTemplateId] =
    useState<string | null>(null);
  const [isCreateClusterSettingsVisible, setIsCreateClusterSettingsVisible] =
    useState(false);
  const isClusterCreating = useSelector(isClusterCreatingSelector);
  const isClusterResizing = useSelector(isClusterResizingSelector);
  const isClusterDeleting = useSelector(isClusterDeletingSelector);
  const isOperationInProgress =
    isClusterCreating || isClusterResizing || isClusterDeleting;
  const previousIsOperationInProgress = usePrevious(isOperationInProgress);
  const keyPair = useSelector(keyPairSelector);
  const previousKeyPair = usePrevious(keyPair);
  const isKeyPairCreating = useSelector(isKeyPairCreatingSelector);
  const isPreviousKeyPairCreating = usePrevious(isKeyPairCreating);
  const [isKeyPairDialogOpened, setIsKeyPairDialogOpened] = useState(false);
  const [isCreateKeyPairDialogOpened, setIsCreateKeyPairDialogOpened] =
    useState(false);

  const latestClusterTemplate = useMemo(() => {
    if (!clusterTemplates?.length) {
      return null;
    }

    const parseVersion = (versionName: string): number[] | null => {
      const versionParts = versionName.replace(/^v/i, "").split(".");
      if (versionParts.some((part) => !/^\d+$/.test(part))) {
        return null;
      }

      return versionParts.map(Number);
    };

    const compareVersions = (leftName: string, rightName: string): number => {
      const leftVersion = parseVersion(leftName);
      const rightVersion = parseVersion(rightName);

      if (!leftVersion || !rightVersion) {
        return leftName.localeCompare(rightName, undefined, { numeric: true });
      }

      for (
        let i = 0;
        i < Math.max(leftVersion.length, rightVersion.length);
        i++
      ) {
        const leftPart = leftVersion[i] ?? 0;
        const rightPart = rightVersion[i] ?? 0;

        if (leftPart !== rightPart) {
          return leftPart - rightPart;
        }
      }

      return 0;
    };

    return clusterTemplates.reduce((latestTemplate, clusterTemplate) =>
      compareVersions(clusterTemplate.name, latestTemplate.name) > 0
        ? clusterTemplate
        : latestTemplate
    );
  }, [clusterTemplates]);

  const resetCreateClusterTemplateState = useCallback(() => {
    setSelectedCreateClusterTemplateId(null);
    setIsCreateClusterSettingsVisible(false);
    dispatch(clustersActions.clearClusterTemplateSettings());
  }, [dispatch]);

  const selectCreateClusterTemplate = useCallback(
    (clusterTemplateId: string | null) => {
      setSelectedCreateClusterTemplateId(clusterTemplateId);

      if (!clusterTemplateId) {
        dispatch(clustersActions.clearClusterTemplateSettings());
        return;
      }

      dispatch(
        clustersActions.getClusterTemplateSettingsRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          clusterTemplateId
        })
      );
    },
    [dispatch, matchParams.projectId, matchParams.regionId]
  );

  const handleCloseDialog = useCallback(() => {
    setDialog({
      ...dialog,
      isOpened: false
    });
    setSelectedItemId(null);
    resetCreateClusterTemplateState();
  }, [dialog, resetCreateClusterTemplateState]);

  const breadcrumbs: Breadcrumb[] = [
    { text: "Organizations", url: ROUTES.ORGANIZATIONS },
    {
      text: organization?.name || "",
      url: generatePath(ROUTES.ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: "Projects",
      url: generatePath(ROUTES.ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: project?.name || "",
      url: generatePath(ROUTES.PROJECT, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId
      })
    },
    {
      text: "Clusters",
      url: generatePath(ROUTES.CLUSTERS, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId
      })
    }
  ];

  const generateTableItemURL = useCallback(
    (id: string) =>
      generatePath(ROUTES.CLUSTER, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId,
        clusterId: id
      }),
    [matchParams.projectId, matchParams.organizationId, matchParams.regionId]
  );

  const handleCreateClusterButtonClick = useCallback(() => {
    if (latestClusterTemplate) {
      selectCreateClusterTemplate(latestClusterTemplate.uuid);
    } else {
      resetCreateClusterTemplateState();
    }

    setDialog({
      type: DIALOG_TYPES.CREATE,
      isOpened: true
    });
  }, [
    latestClusterTemplate,
    resetCreateClusterTemplateState,
    selectCreateClusterTemplate
  ]);

  const handleDeleteClusterMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.DELETE,
      isOpened: true
    });
  }, []);

  const handleDownloadKubeconfigFileMenuItemClick = useCallback(
    (id: string) => {
      downloadFile(
        `${API_PATH}gotham-${matchParams.regionId}-kubernetes/method/${matchParams.projectId}/clusters/${id}/kubeconfig`
      );
      dispatch(
        notificationsActions.showNotification({
          title: "Kubeconfig file is generating. Download will start soon.",
          type: NOTIFICATION_TYPES.INFO
        })
      );
    },
    [matchParams.regionId, matchParams.projectId, dispatch]
  );

  const tableActions: TableRowActionsMenuItem<TableCluster>[] = [
    {
      label: "Download kubeconfig file",
      handler: handleDownloadKubeconfigFileMenuItemClick
    },
    {
      label: "Delete",
      handler: handleDeleteClusterMenuItemClick
    }
  ];

  useMount(() => {
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.clusters}`,
        actionCreator: clustersActions.getClustersRequest,
        params: {
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!
        }
      })
    );
    dispatch(
      enterprisesActions.getOrganizationRequest({
        id: matchParams.organizationId!
      })
    );
    dispatch(
      projectsActions.getProjectRequest({
        regionId: matchParams.regionId!,
        id: matchParams.projectId!
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.flavors}`,
        actionCreator: instancesActions.getFlavorsRequest,
        params: {
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          type: FLAVOR_TYPE.CLUSTER
        }
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.clusterTemplates}`,
        actionCreator: clustersActions.getClusterTemplatesRequest,
        params: {
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!
        }
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.keyPairs}`,
        actionCreator: securityActions.getKeyPairsRequest,
        params: {
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!
        }
      })
    );
  });

  useUnmount(() => {
    Object.values(POLL_IDS).forEach((id) => {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${id}`
        })
      );
    });
    dispatch(enterprisesActions.clear());
    dispatch(projectsActions.clear());
    dispatch(clustersActions.clear());
    dispatch(instancesActions.clear());
    dispatch(securityActions.clear());
  });

  useEffect(() => {
    if (previousIsOperationInProgress && !isOperationInProgress) {
      dispatch(
        clustersActions.getClustersRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!
        })
      );
    }
  }, [
    previousIsOperationInProgress,
    isOperationInProgress,
    dispatch,
    matchParams.projectId,
    matchParams.regionId
  ]);

  useEffect(() => {
    if (isPreviousKeyPairCreating && !isKeyPairCreating) {
      dispatch(
        securityActions.getKeyPairsRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!
        })
      );
    }
  }, [
    isPreviousKeyPairCreating,
    isKeyPairCreating,
    dispatch,
    matchParams.projectId,
    matchParams.regionId
  ]);

  const isCreateClusterConfigurationHidden = useCallback(
    (fieldValues: Record<string, unknown>) => !fieldValues.cluster_template_id,
    []
  );

  const createClusterSettings = useMemo(
    () =>
      (clusterTemplateSettings || []).filter(
        (setting) => setting.key !== "auto_scaling_enabled"
      ),
    [clusterTemplateSettings]
  );

  const handleCreateClusterTemplateChange = useCallback(
    ({ value }: { value: unknown }) => {
      const clusterTemplateId = (value as SelectOption | null)?.value || null;
      selectCreateClusterTemplate(clusterTemplateId);
    },
    [selectCreateClusterTemplate]
  );

  const normalizeClusterSettingFormValue = useCallback(
    (setting: ClusterSetting, value: unknown): string | undefined => {
      const stringifyPrimitiveValue = (
        primitiveValue: unknown
      ): string | undefined => {
        if (typeof primitiveValue === "string") {
          return primitiveValue;
        }

        if (
          typeof primitiveValue === "number" ||
          typeof primitiveValue === "boolean"
        ) {
          return String(primitiveValue);
        }

        return undefined;
      };

      switch (setting.value_type) {
        case "bool":
          return typeof value === "boolean"
            ? String(value)
            : stringifyPrimitiveValue(value);
        case "list":
          if (typeof value === "object" && value !== null && "value" in value) {
            const optionValue = value.value;
            return typeof optionValue === "string" ? optionValue : undefined;
          }

          return undefined;
        case "int":
          return value === "" || value == null
            ? undefined
            : stringifyPrimitiveValue(value);
        case "string":
        default:
          return stringifyPrimitiveValue(value);
      }
    },
    []
  );

  const isCreateClusterAutoScalingDisabled = useCallback(
    (fieldValues: Record<string, unknown>) =>
      fieldValues["auto_scaling_enabled"] !== true,
    []
  );

  const isCreateClusterTemplateSettingsHidden = useCallback(
    (fieldValues: Record<string, unknown>) =>
      isCreateClusterConfigurationHidden(fieldValues) ||
      !isCreateClusterSettingsVisible,
    [isCreateClusterConfigurationHidden, isCreateClusterSettingsVisible]
  );

  const handleToggleCreateClusterSettings = useCallback(() => {
    setIsCreateClusterSettingsVisible((currentValue) => !currentValue);
  }, []);

  const createClusterSettingsFields = useMemo<FormDialogField[]>(() => {
    if (!selectedCreateClusterTemplateId) {
      return [];
    }

    if (areClusterTemplateSettingsLoading) {
      return [
        {
          name: "cluster_template_settings_loading",
          type: FIELD_TYPES.LABEL,
          label: "Loading template settings...",
          isHidden: isCreateClusterTemplateSettingsHidden
        }
      ];
    }

    if (!clusterTemplateSettings) {
      return [];
    }

    if (createClusterSettings.length === 0) {
      return [
        {
          name: "cluster_template_settings_empty",
          type: FIELD_TYPES.LABEL,
          label:
            "No additional settings available for the selected cluster template.",
          isHidden: isCreateClusterTemplateSettingsHidden
        }
      ];
    }

    const groupedSettings = createClusterSettings.reduce<
      { group: string; settings: ClusterSetting[] }[]
    >((groups, setting) => {
      const existingGroup = groups.find(
        (group) => group.group === setting.group
      );
      if (existingGroup) {
        existingGroup.settings.push(setting);
        return groups;
      }

      return [...groups, { group: setting.group, settings: [setting] }];
    }, []);

    return groupedSettings.flatMap(({ group, settings }) => {
      const fields = settings.map((setting) => {
        if (setting.value_type === "bool") {
          return {
            name: setting.key,
            type: FIELD_TYPES.TOGGLE,
            label: setting.description,
            defaultValue: setting.value.toLowerCase() === "true",
            isHidden: isCreateClusterTemplateSettingsHidden
          };
        }

        if (setting.value_type === "int") {
          return {
            name: setting.key,
            type: FIELD_TYPES.NUMBER,
            label: setting.description,
            defaultValue: setting.value === "" ? "" : Number(setting.value),
            min: setting.min,
            max: setting.max,
            isHidden: isCreateClusterTemplateSettingsHidden
          };
        }

        if (setting.value_type === "list") {
          const options = (setting.allowed_values || []).map((value) => ({
            label: value,
            value
          }));

          return {
            name: setting.key,
            type: FIELD_TYPES.SELECT,
            label: setting.description,
            options,
            defaultValue:
              options.find((option) => option.value === setting.value) || null,
            isHidden: isCreateClusterTemplateSettingsHidden
          };
        }

        return {
          name: setting.key,
          type: FIELD_TYPES.TEXT,
          label: setting.description,
          defaultValue: setting.value,
          isHidden: isCreateClusterTemplateSettingsHidden
        };
      });

      return [
        {
          name: `cluster_template_settings_group_${group}`,
          type: FIELD_TYPES.LABEL,
          label: group,
          isHidden: isCreateClusterTemplateSettingsHidden
        },
        ...fields
      ];
    });
  }, [
    areClusterTemplateSettingsLoading,
    clusterTemplateSettings,
    createClusterSettings,
    isCreateClusterTemplateSettingsHidden,
    selectedCreateClusterTemplateId
  ]);

  const handleConfirmCreateCluster = useCallback(
    (data: {
      cluster_template_id: SelectOption;
      docker_volume_size: string;
      flavor_id: SelectOption;
      keypair: SelectOption;
      master_count: string;
      master_flavor_id: SelectOption;
      name: string;
      node_count: string;
      floating_ip_enabled: boolean;
      auto_scaling_enabled: boolean;
      min_node_count?: string;
      max_node_count?: string;
      [key: string]: unknown;
    }) => {
      const resolvedNodeCount = data.auto_scaling_enabled
        ? Number(data.min_node_count ?? MIN_NODE_COUNT)
        : Number(data.node_count);

      const labels = createClusterSettings.reduce<Record<string, string>>(
        (acc, setting) => {
          const value = normalizeClusterSettingFormValue(
            setting,
            data[setting.key]
          );
          if (typeof value === "undefined") {
            return acc;
          }

          const defaultValue =
            setting.value_type === "bool"
              ? setting.default_value.toLowerCase()
              : setting.default_value;

          if (value !== defaultValue) {
            acc[setting.key] = value;
          }

          return acc;
        },
        {}
      );

      dispatch(
        clustersActions.createClusterRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          data: {
            name: data.name,
            cluster_template_id: data.cluster_template_id.value,
            docker_volume_size: Number(data.docker_volume_size),
            flavor_id: data.flavor_id.value,
            keypair: data.keypair.value,
            master_count: Number(data.master_count),
            master_flavor_id: data.master_flavor_id.value,
            node_count: resolvedNodeCount,
            floating_ip_enabled: !data.floating_ip_enabled,
            auto_scaling_enabled: data.auto_scaling_enabled,
            ...(data.min_node_count !== undefined && data.min_node_count !== ""
              ? { min_node_count: Number(data.min_node_count) }
              : {}),
            ...(data.max_node_count !== undefined && data.max_node_count !== ""
              ? { max_node_count: Number(data.max_node_count) }
              : {}),
            ...(Object.keys(labels).length > 0 ? { labels } : {})
          }
        })
      );
      handleCloseDialog();
    },
    [
      createClusterSettings,
      dispatch,
      handleCloseDialog,
      matchParams.projectId,
      matchParams.regionId,
      normalizeClusterSettingFormValue
    ]
  );

  const handleConfirmDeleteCluster = useCallback(() => {
    if (selectedItemId) {
      dispatch(
        clustersActions.deleteClusterRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          id: selectedItemId
        })
      );
    }
    handleCloseDialog();
  }, [
    dispatch,
    selectedItemId,
    matchParams.projectId,
    handleCloseDialog,
    matchParams.regionId
  ]);

  const handleCreateKeyPair = useCallback(() => {
    setIsCreateKeyPairDialogOpened(true);
  }, []);

  const handleCloseCreateKeyPairDialog = useCallback(() => {
    setIsCreateKeyPairDialogOpened(false);
  }, []);

  const handleConfirmCreateKeyPair = useCallback(
    (data: { name: string }) => {
      dispatch(
        securityActions.createKeyPairRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          data
        })
      );
      handleCloseCreateKeyPairDialog();
    },
    [
      dispatch,
      handleCloseCreateKeyPairDialog,
      matchParams.projectId,
      matchParams.regionId
    ]
  );

  const handleCloseKeyPairDialog = useCallback(() => {
    setIsKeyPairDialogOpened(false);
  }, []);

  useEffect(() => {
    if (previousKeyPair !== keyPair && keyPair) {
      setIsKeyPairDialogOpened(true);
    }
  }, [
    previousKeyPair,
    keyPair,
    dispatch,
    matchParams.projectId,
    matchParams.regionId
  ]);

  const previousSelectedItemId = usePrevious(selectedItemId);
  const deletingItemId = selectedItemId
    ? selectedItemId
    : previousSelectedItemId;
  const deletingClusterName = clusters?.find(
    (cluster) => cluster.id === deletingItemId
  )?.name;

  const createClusterDialogFields = useMemo<FormDialogField[]>(
    () => [
      {
        name: "name",
        type: FIELD_TYPES.TEXT,
        label: "Name",
        rules: string()
          .required()
          .matches(
            REGEX.NAME_STARTS_WITH_LETTER,
            ERROR_MESSAGES.NAME_STARTS_WITH_LETTER
          )
          .matches(REGEX.CLUSTER_NAME, ERROR_MESSAGES.CLUSTER_NAME)
      },
      {
        name: "cluster_template_id",
        type: FIELD_TYPES.SELECT,
        label: "Cluster template",
        helperText: selectedCreateClusterTemplateId ? (
          <SectionToggleButton
            isExpanded={isCreateClusterSettingsVisible}
            label={"Cluster settings"}
            onClick={handleToggleCreateClusterSettings}
          />
        ) : undefined,
        options: clusterTemplates?.map((clusterTemplate) =>
          getSelectOption(clusterTemplate, "name", "uuid")
        ),
        defaultValue: latestClusterTemplate
          ? getSelectOption(latestClusterTemplate, "name", "uuid")
          : null,
        onChange: handleCreateClusterTemplateChange,
        rules: selectOptionSchema
      },
      ...createClusterSettingsFields,
      {
        name: "master_flavor_id",
        type: FIELD_TYPES.SELECT,
        label: "Master flavor",
        options: flavors?.map((flavor) =>
          getSelectOption(flavor, "name", "id")
        ),
        defaultValue: () =>
          default_flavor ? getSelectOption(default_flavor, "name", "id") : null,
        isHidden: isCreateClusterConfigurationHidden,
        rules: selectOptionSchema
      },
      {
        name: "flavor_id",
        type: FIELD_TYPES.SELECT,
        label: "Node flavor",
        options: flavors?.map((flavor) =>
          getSelectOption(flavor, "name", "id")
        ),
        defaultValue: () =>
          default_flavor ? getSelectOption(default_flavor, "name", "id") : null,
        isHidden: isCreateClusterConfigurationHidden,
        rules: selectOptionSchema
      },
      {
        name: "keypair",
        type: FIELD_TYPES.SELECT,
        label: "Key pair",
        onCreate: handleCreateKeyPair,
        createText: "Create new key pair",
        options: keyPairs?.map((keyPair) =>
          getSelectOption(keyPair, "name", "id")
        ),
        defaultValue:
          keyPairs?.length === 1
            ? getSelectOption(keyPairs[0], "name", "id")
            : undefined,
        isHidden: isCreateClusterConfigurationHidden,
        rules: selectOptionSchema
      },
      {
        name: "docker_volume_size",
        type: FIELD_TYPES.NUMBER,
        defaultValue: MIN_DOCKER_VOLUME_SIZE,
        label: "Docker image size",
        suffix: "GiB",
        step: VOLUME_SIZE_STEP,
        min: MIN_DOCKER_VOLUME_SIZE,
        isHidden: isCreateClusterConfigurationHidden,
        rules: number()
          .integer()
          .min(
            MIN_DOCKER_VOLUME_SIZE,
            `Docker image size must be greater than or equals to ${MIN_DOCKER_VOLUME_SIZE} GiB.`
          )
          .test(
            "isMultipleOf",
            `Docker image size must be multiple of ${VOLUME_SIZE_STEP}.`,
            (value) => Boolean(value && value % VOLUME_SIZE_STEP === 0)
          )
          .required()
      },
      {
        name: "master_count",
        type: FIELD_TYPES.NUMBER,
        defaultValue: 3,
        helperText:
          "ℹ️ For production environments, use at least 3 Master nodes. Prefer odd numbers (e.g. 3, 5)",
        label: "Master count",
        min: MIN_MASTER_COUNT,
        isHidden: isCreateClusterConfigurationHidden,
        rules: number()
          .integer()
          .min(
            MIN_MASTER_COUNT,
            `Master count must be greater than or equals to ${MIN_MASTER_COUNT}.`
          )
          .required()
      },
      {
        name: "node_count",
        type: FIELD_TYPES.NUMBER,
        defaultValue: MIN_NODE_COUNT,
        label: "Node count",
        min: MIN_NODE_COUNT,
        isHidden: (fieldValues: Record<string, unknown>) =>
          isCreateClusterConfigurationHidden(fieldValues) ||
          !isCreateClusterAutoScalingDisabled(fieldValues),
        rules: number()
          .integer()
          .min(
            MIN_NODE_COUNT,
            `Node count must be greater than or equals to ${MIN_NODE_COUNT}.`
          )
          .when("auto_scaling_enabled", {
            is: false,
            then: (schema) => schema.required()
          })
      },
      {
        name: "auto_scaling_enabled",
        type: FIELD_TYPES.CHECKBOX,
        label: "Enable auto-scaling",
        defaultValue: DEFAULT_CLUSTER_AUTO_SCALING,
        isHidden: isCreateClusterConfigurationHidden,
        onChange: ({
          value,
          setValue,
          fieldValues
        }: {
          value: unknown;
          setValue: (name: string, value: unknown) => void;
          fieldValues: Record<string, unknown>;
        }) => {
          if (value === true) {
            const nodeCount = fieldValues["node_count"];
            setValue(
              "min_node_count",
              typeof nodeCount === "number" || typeof nodeCount === "string"
                ? nodeCount
                : MIN_NODE_COUNT
            );
            return;
          }

          const minNodeCount = fieldValues["min_node_count"];
          setValue(
            "node_count",
            typeof minNodeCount === "number" || typeof minNodeCount === "string"
              ? minNodeCount
              : MIN_NODE_COUNT
          );
        }
      },
      {
        name: "min_node_count",
        type: FIELD_TYPES.NUMBER,
        min: MIN_AUTOSCALING_NODE_COUNT,
        max: MAX_NODE_COUNT,
        defaultValue: MIN_NODE_COUNT,
        rules: number()
          .integer()
          .min(
            MIN_AUTOSCALING_NODE_COUNT,
            `Node count must be greater than or equals to ${MIN_AUTOSCALING_NODE_COUNT}.`
          )
          .when("auto_scaling_enabled", {
            is: true,
            then: (schema) =>
              schema
                .required()
                .when(
                  "max_node_count",
                  (maxNodeCount: number[], maxSchema: NumberSchema) => {
                    return Number.isInteger(maxNodeCount[0])
                      ? maxSchema.max(
                          ref("max_node_count"),
                          `Node count must be less than ${maxNodeCount[0]}.`
                        )
                      : maxSchema.max(
                          MAX_NODE_COUNT,
                          `Node count must be less than ${MAX_NODE_COUNT}.`
                        );
                  }
                )
          }),
        isHidden: (fieldValues: Record<string, unknown>) =>
          isCreateClusterConfigurationHidden(fieldValues) ||
          isCreateClusterAutoScalingDisabled(fieldValues),
        label: "Minimum node count"
      },
      {
        name: "max_node_count",
        type: FIELD_TYPES.NUMBER,
        max: MAX_NODE_COUNT,
        min: MIN_NODE_COUNT,
        defaultValue: MAX_NODE_COUNT,
        rules: number()
          .integer()
          .when("auto_scaling_enabled", {
            is: true,
            then: (schema) =>
              schema
                .required()
                .when(
                  "min_node_count",
                  (minNodeCount: number[], minSchema: NumberSchema) => {
                    return Number.isInteger(minNodeCount[0]) &&
                      minNodeCount[0] <= MAX_NODE_COUNT
                      ? minSchema.min(
                          ref("min_node_count"),
                          `Node count must be greater than or equals to ${minNodeCount[0]}.`
                        )
                      : minSchema.min(
                          MIN_NODE_COUNT,
                          `Node count must be greater than or equals to ${MIN_NODE_COUNT}.`
                        );
                  }
                )
                .max(
                  MAX_NODE_COUNT,
                  `Node count must be less than ${MAX_NODE_COUNT}.`
                )
          }),
        label: "Maximum node count",
        isHidden: (fieldValues: Record<string, unknown>) =>
          isCreateClusterConfigurationHidden(fieldValues) ||
          isCreateClusterAutoScalingDisabled(fieldValues)
      },
      {
        name: "floating_ip_enabled",
        type: FIELD_TYPES.CHECKBOX,
        label: "Accessible on private network only",
        isHidden: isCreateClusterConfigurationHidden
      }
    ],
    [
      clusterTemplates,
      createClusterSettingsFields,
      default_flavor,
      flavors,
      handleCreateClusterTemplateChange,
      handleCreateKeyPair,
      handleToggleCreateClusterSettings,
      isCreateClusterAutoScalingDisabled,
      isCreateClusterConfigurationHidden,
      isCreateClusterSettingsVisible,
      keyPairs,
      latestClusterTemplate,
      selectedCreateClusterTemplateId
    ]
  );

  const dialogProps: {
    [key in DIALOG_TYPES]?: Omit<FormDialogProps, "isOpened" | "onCancel">;
  } = {
    [DIALOG_TYPES.CREATE]: {
      onConfirm: handleConfirmCreateCluster,
      title: "Create cluster",
      confirmButtonLabel: "Create",
      yupNoSortEdges: [["min_node_count", "max_node_count"]],
      isLocked: isKeyPairCreating,
      fields: createClusterDialogFields
    },
    [DIALOG_TYPES.DELETE]: {
      isLocked: false,
      onConfirm: handleConfirmDeleteCluster,
      title: `Are you sure you want to delete "${
        deletingClusterName ?? "selected"
      }" cluster?`,
      fields: [
        {
          name: "confirmationName",
          type: FIELD_TYPES.TEXT,
          label: "Type the cluster name to confirm deletion",
          rules: string()
            .required()
            .test({
              name: "validateConfirmationName",
              message: "Cluster name does not match",
              test: function (value) {
                return value === deletingClusterName;
              }
            })
        }
      ],
      confirmButtonLabel: "Delete"
    }
  };

  return (
    <>
      <Head title={title} />
      {organization && project && <Breadcrumbs breadcrumbs={breadcrumbs} />}
      <Typography variant={"h4"} component={"h2"}>
        {title}
      </Typography>
      <Table
        isSearchEnabled={true}
        isSortingEnabled={true}
        rows={clusters || []}
        columns={tableColumns}
        actions={tableActions}
        itemLink={{
          column: "name",
          getURL: generateTableItemURL
        }}
        isLoading={!clusters}
        toolbarItems={
          <Button
            disabled={!keyPairs || !clusterTemplates || !flavors}
            onClick={handleCreateClusterButtonClick}
            variant={"contained"}
          >
            Create cluster
          </Button>
        }
      />
      <FormDialog
        isOpened={dialog.isOpened}
        onCancel={handleCloseDialog}
        isLocked={dialogProps[dialog.type]?.isLocked ?? false}
        fields={dialogProps[dialog.type]?.fields ?? []}
        onConfirm={dialogProps[dialog.type]?.onConfirm ?? (() => {})}
        title={dialogProps[dialog.type]?.title ?? "Dialog"}
        confirmButtonLabel={
          dialogProps[dialog.type]?.confirmButtonLabel ?? "Confirm"
        }
        yupNoSortEdges={dialogProps[dialog.type]?.yupNoSortEdges}
      />
      <FormDialog
        isOpened={isCreateKeyPairDialogOpened}
        onCancel={() => setIsCreateKeyPairDialogOpened(false)}
        isLocked={isKeyPairCreating}
        fields={[
          {
            name: "name",
            type: FIELD_TYPES.TEXT,
            label: "Name",
            rules: string()
              .required()
              .test({
                name: "validateName",
                test: validateName(ENTITY_NAME_LENGTH)
              })
              .test(
                "sameNameCheckFormat",
                "This name already exists",
                (value) => {
                  const name = keyPairs?.find(
                    (keypair) => keypair.id === value
                  )?.name;
                  return Boolean(value && !name);
                }
              )
          }
        ]}
        onConfirm={handleConfirmCreateKeyPair}
        title={"Create New Key Pair"}
        confirmButtonLabel={"Create"}
        yupNoSortEdges={undefined}
      />
      {keyPair && isString(keyPair.private_key) && (
        <KeyPairDialog
          isOpened={isKeyPairDialogOpened}
          name={keyPair.name}
          privateKey={keyPair.private_key}
          onClose={handleCloseKeyPairDialog}
        />
      )}
    </>
  );
};
